import React, { useEffect, useState } from "react";
import { getEmployeeData, getEmployeeId } from "../../utils/localStorage";
import { getRequest, postRequest, putRequest } from "../../service";
import { resolveRelaxableRuleType } from "../../modals/AttendanceRules";

// Convert 24-hour time string ("18:13:30") to 12-hour format with AM/PM
function formatTime24to12(time24) {
  if (!time24 || time24 === "-") return "-";
  const [h, m, s] = time24.split(":");
  let hour = parseInt(h, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  return `${hour.toString().padStart(2, "0")}:${m}:${s} ${ampm}`;
}

// Pad single digit numbers with leading zero
function pad(num) {
  return num.toString().padStart(2, "0");
}

// Calculate duration from startTime (string "HH:mm:ss") to now, as "HH:mm:ss"
function getDuration(startTime) {
  if (!startTime || startTime === "-") return "00:00:00";
  const now = new Date();
  const [h, m, s] = startTime.split(":").map(Number);
  
  // Create start time for today
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m, s);
  
  // If start time is in the future (e.g., check-in was yesterday), adjust to yesterday
  let start = startToday;
  if (startToday > now) {
    start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, h, m, s);
  }
  
  let diff = Math.floor((now - start) / 1000);
  if (diff < 0) diff = 0;
  
  const hours = pad(Math.floor(diff / 3600));
  const minutes = pad(Math.floor((diff % 3600) / 60));
  const seconds = pad(diff % 60);
  return `${hours}:${minutes}:${seconds}`;
}

// Convert time string to minutes for easier comparison
const timeToMinutes = (timeStr) => {
  if (!timeStr) return 0;
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
};

// Apply attendance rules and calculate status
const applyAttendanceRules = (checkIn, checkOut, shift) => {
  if (!shift || !shift.settings) return "Present";

  const settings = shift.settings;
  let status = "Present";

  // Convert times to minutes for comparison
  const startTimeMinutes = timeToMinutes(shift.startTime);
  const endTimeMinutes = timeToMinutes(shift.endTime);
  const checkInMinutes = timeToMinutes(checkIn);
  const checkOutMinutes = timeToMinutes(checkOut);

  // Handle overnight shifts
  let adjustedEndTimeMinutes = endTimeMinutes;
  let adjustedCheckOutMinutes = checkOutMinutes;
  
  if (shift.dayChange && endTimeMinutes < startTimeMinutes) {
    // For overnight shifts, if checkout is before start time, add 24 hours
    if (checkOutMinutes < startTimeMinutes) {
      adjustedCheckOutMinutes = checkOutMinutes + 24 * 60;
    }
    adjustedEndTimeMinutes = endTimeMinutes + 24 * 60;
  }

  // Apply Attendance Rules (Late Rules) - skipped when relaxable settings are enabled
  if (settings.isAttendanceRulesEnabled && !settings.isRelaxableSettingsEnabled) {
    if (settings.lateRuleType === 'time') {
      // Rule 2: Time-based late detection
      const lateThresholdMinutes = startTimeMinutes + settings.rule2.lateMinutes;
      
      if (checkInMinutes > lateThresholdMinutes) {
        status = 'Late';
      }
    } else if (settings.lateRuleType === 'count') {
      // Rule 1: Count-based late detection
      const lateThresholdMinutes = startTimeMinutes + settings.rule1.lateMinutes;
      
      if (checkInMinutes > lateThresholdMinutes) {
        status = 'Late';
      }
    }
  }

  // Apply Early Out Rules - skipped when relaxable settings are enabled
  if (settings.isEarlyOutRulesEnabled && !settings.isRelaxableSettingsEnabled) {
    const earlyOutThresholdMinutes = adjustedEndTimeMinutes - settings.earlyOutMinutes;
    
    // Only mark as early out if checkout is BEFORE the early out threshold
    if (adjustedCheckOutMinutes < earlyOutThresholdMinutes) {
      if (status === 'Late') {
        status = 'Late & Early Out';
      } else {
        status = 'Early Out';
      }
    }
  }

  // Apply Relaxable Settings
  if (settings.isRelaxableSettingsEnabled && settings.relaxable) {
    const ruleType = resolveRelaxableRuleType(settings.relaxable);
    const graceMinutes = Number(settings.relaxable.flexibleStartHours) || 0;
    const flexibleStartMinutes = startTimeMinutes + graceMinutes;

    if (ruleType === 'lateArrival') {
      if (checkInMinutes > flexibleStartMinutes) {
        status = 'Late';
      }
    } else if (ruleType === 'detectHourly') {
      let checkOut = adjustedCheckOutMinutes;
      if (shift.dayChange && checkOut < checkInMinutes) {
        checkOut += 24 * 60;
      }
      const workingMinutes = checkOut - checkInMinutes;
      const requiredMinutes = (shift.workingHours ?? 9) * 60;

      if (workingMinutes < requiredMinutes) {
        status = 'Incomplete';
      }
    }
  }

  return status;
};

const AttendanceCard = () => {

  const [runningTime, setRunningTime] = useState("00:00:00");
  const [shiftData, setShiftData] = useState(null);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [attendanceData, setAttendanceData] = useState(null);
  const [latestLog, setLatestLog] = useState(null);
  const employeeData = getEmployeeData();

  // Only show if latestLog exists, checkIn is valid, and checkOut is "-"
  const showCheckIn =
    latestLog &&
    latestLog.checkIn &&
    latestLog.checkIn !== "-" &&
    latestLog.checkOut === "-";

  // Check if there's no check-in today and attendanceWeb is enabled
  const showCheckInButton = !showCheckIn && employeeData?.attendanceWeb === true;

  // Check if attendanceWeb is enabled and user has checked in (for showing check out button)
  const showCheckOutButton = showCheckIn && employeeData?.attendanceWeb === true;

  // Check if attendanceWeb is false and user has checked in (for showing original sign out button)
  const showOriginalSignOut = showCheckIn && !employeeData?.attendanceWeb;

  // Fetch attendance data for current day
  const fetchAttendanceData = async () => {
    try {
      const employeeId = getEmployeeId();
      if (!employeeId) {
        console.error("Employee ID not found");
        return;
      }

      const today = new Date();
      const currentDate = today.toISOString().split('T')[0]; // Format: "2025-08-08"
      
      // For cross-day shifts, we need to check both current day and previous day
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayDate = yesterday.toISOString().split('T')[0];
      
      // Fetch attendance for both current day and previous day
      const response = await getRequest(`/api/myattendance?employeeId=${employeeId}&startDate=${yesterdayDate}&endDate=${currentDate}`);
      
      if (response.attendanceLogs && response.attendanceLogs.length > 0) {
        setAttendanceData(response.attendanceLogs);
        // Find the latest log
        const latest = response.attendanceLogs.reduce((latest, log) => {
          return !latest || new Date(log.date) > new Date(latest.date) ? log : latest;
        }, null);
        setLatestLog(latest);
      } else {
        setAttendanceData([]);
        setLatestLog(null);
      }
    } catch (error) {
      console.error("Error fetching attendance data:", error);
      setError("Failed to load attendance data");
    }
  };

  // Fetch shift data and employee details when component mounts
  useEffect(() => {
    const fetchData = async () => {
      try {
        const employeeId = getEmployeeId();
        if (!employeeId) {
          console.error("Employee ID not found");
          return;
        }

        // Fetch shift data
        const shiftResponse = await getRequest(`/api/companies/shifts/byEmployeeId?employeeId=${employeeId}`);
        setShiftData(shiftResponse.shift);

        // Fetch employee details
        const employeeResponse = await getRequest(`/api/employees/${employeeId}`);
        setEmployeeDetails(employeeResponse.employee);

        
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load data");
      }
    };

    // Fetch attendance data on component mount
    fetchAttendanceData();

    if (showCheckInButton) {
      fetchData();
    }
  }, []);

  // Handle check-in
  const handleCheckIn = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const employeeId = getEmployeeId();
      const companyId = employeeData?.companyId;
      
      if (!employeeId || !companyId) {
        throw new Error("Employee ID or Company ID not found");
      }

      const now = new Date();
      const checkInTime = now.toTimeString().split(' ')[0]; // Format: "17:56:44"
      
      // Determine the correct date for check-in based on shift timing
      let checkInDate;
      if (shiftData && shiftData.dayChange) {
        // For overnight shifts, check-in date is the current date
        // (evening shift starts on the same day)
        checkInDate = now.toISOString().split('T')[0];
      } else {
        // Regular shift (same day start and end)
        checkInDate = now.toISOString().split('T')[0];
      }
      
      const payload = {
        employeeId: employeeId,
        machineId: employeeDetails?.machineCode,
        companyId: companyId,
        checkIn: checkInTime,
        date: checkInDate,
        reqHour: shiftData?.workingHours,
        checkOut: "-",
        status: "Missing",
        workingHour: shiftData?.workingHours,
        overtime: 0,
        perDaySelery: employeeDetails?.perday_salary || 0 
      };

      const response = await postRequest("/api/myattendance", payload);
      
      if (response.message === "Attendance log created successfully") {
        await fetchAttendanceData();
      } else {
        setError(response.message || "Check-in failed");
      }
    } catch (error) {
      console.error("Error during check-in:", error);
      setError("Check-in failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle check-out
  const handleCheckOut = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const employeeId = getEmployeeId();
      const companyId = employeeData?.companyId;
      
      if (!employeeId || !companyId) {
        throw new Error("Employee ID or Company ID not found");
      }

      if (!latestLog || !latestLog._id) {
        throw new Error("No active attendance session found");
      }

      const now = new Date();
      const checkOutTime = now.toTimeString().split(' ')[0]; // Format: "17:56:44"
      
      // For check-out, determine the correct date based on shift type
      let checkOutDate;
      if (shiftData && shiftData.dayChange) {
        // For overnight shifts, check-out date depends on the time
        // If check-out time is before start time (e.g., 02:00), it's the next day
        // If check-out time is after start time (e.g., 20:00), it's the same day
        const startTimeMinutes = timeToMinutes(shiftData.startTime);
        const checkOutMinutes = timeToMinutes(checkOutTime);
        
        if (checkOutMinutes < startTimeMinutes) {
          // Check-out time is before start time (e.g., 02:00 < 18:00)
          // This means it's the next day
          const tomorrow = new Date(now);
          tomorrow.setDate(tomorrow.getDate() + 1);
          checkOutDate = tomorrow.toISOString().split('T')[0];
        } else {
          // Check-out time is after start time (e.g., 20:00 > 18:00)
          // This means it's the same day
          checkOutDate = now.toISOString().split('T')[0];
        }
      } else {
        // Regular shift (same day start and end)
        checkOutDate = now.toISOString().split('T')[0];
      }
      
      // Calculate working hours from check-in to check-out
      const checkInMinutes = timeToMinutes(latestLog.checkIn);
      const checkOutMinutes = timeToMinutes(checkOutTime);
      
      let workingMinutes = 0;
      
      if (shiftData && shiftData.dayChange && timeToMinutes(shiftData.endTime) < timeToMinutes(shiftData.startTime)) {
        // Handle overnight shifts
        let adjustedCheckInMinutes = checkInMinutes;
        let adjustedCheckOutMinutes = checkOutMinutes;
        const startTimeMinutes = timeToMinutes(shiftData.startTime);
        
        // If check-in is before start time, it's from previous day
        if (checkInMinutes < startTimeMinutes) {
          adjustedCheckInMinutes = checkInMinutes + 24 * 60;
        }
        
        // If check-out is before start time, it's from next day
        if (checkOutMinutes < startTimeMinutes) {
          adjustedCheckOutMinutes = checkOutMinutes + 24 * 60;
        }
        
        workingMinutes = adjustedCheckOutMinutes - adjustedCheckInMinutes;
      } else {
        // Regular shift (same day)
        workingMinutes = checkOutMinutes - checkInMinutes;
      }
      
      // Convert working minutes to hours and format as "HH:mm"
      const workingHours = Math.floor(workingMinutes / 60);
      const workingMins = workingMinutes % 60;
      const workingHourFormatted = `${workingHours.toString().padStart(2, '0')}:${workingMins.toString().padStart(2, '0')}`;
      
      // Calculate overtime
      const requiredHours = shiftData?.workingHours || 0;
      const requiredMinutes = requiredHours * 60;
      let overtimeMinutes = 0;
      
      if (workingMinutes > requiredMinutes) {
        overtimeMinutes = workingMinutes - requiredMinutes;
      }
      
      // Convert overtime minutes to hours and format as "HH:mm"
      const overtimeHours = Math.floor(overtimeMinutes / 60);
      const overtimeMins = overtimeMinutes % 60;
      const overtimeFormatted = `${overtimeHours.toString().padStart(2, '0')}:${overtimeMins.toString().padStart(2, '0')}`;
      
      // Calculate status based on shift rules
      const calculatedStatus = applyAttendanceRules(
        latestLog.checkIn, 
        checkOutTime, 
        shiftData
      );
      
      const payload = {
        checkOut: checkOutTime,
        status: calculatedStatus, // Use calculated status instead of hardcoded "Present"
        date: checkOutDate,
        workingHour: workingHourFormatted,
        overtime: overtimeFormatted
      };

      // Use the attendance ID from the latest log
      const response = await putRequest(`/api/myattendance/${latestLog._id}`, payload);
      
      if (response.message === "Attendance log updated successfully") {
        // Refresh attendance data
        await fetchAttendanceData();
      } else {
        setError(response.message || "Check-out failed");
      }
    } catch (error) {
      console.error("Error during check-out:", error);
      setError("Check-out failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let interval;
    if (showCheckIn) {
      // Update running time every second
      interval = setInterval(() => {
        setRunningTime(getDuration(latestLog.checkIn));
      }, 1000);
      // Set initial value
      setRunningTime(getDuration(latestLog.checkIn));
    } else {
      setRunningTime("00:00:00");
    }
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [showCheckIn, latestLog?.checkIn]);

  if (!showCheckIn && !showCheckInButton) {
    return (
      <div className="w-full">
        <p className="text-md text-center text-gray-700">
          No Check In Today
        </p>
      </div>
    );
  }

  if (showCheckInButton) {
    return (
      <div className="w-full">
        {error && (
          <p className="text-sm text-center text-red-500 mb-2">
            {error}
          </p>
        )}
        <div className="flex justify-center">
          <button 
            className={`text-white text-md px-4 py-2 rounded-md ${
              isLoading 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-primary hover:bg-primary/90'
            }`}
            onClick={handleCheckIn}
            disabled={isLoading}
          >
            {isLoading ? "Checking In..." : "Check In"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <p className="text-md text-center text-gray-700">
        You Signed In Today at{" "}
        <span className="font-semibold">
          {formatTime24to12(latestLog.checkIn)}
        </span>
        !
      </p>
      <p className="text-2xl font-bold text-center text-primary my-4">
        {runningTime}
      </p>
      <div className="flex justify-center">
        {showCheckOutButton ? (
          <button 
            className={`text-white text-md px-4 py-2 rounded-md ${
              isLoading 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-primary hover:bg-primary/90'
            }`}
            onClick={handleCheckOut}
            disabled={isLoading}
          >
            {isLoading ? "Checking Out..." : "Check Out"}
          </button>
        ) : showOriginalSignOut ? (
          <button className="text-white text-md px-4 py-2 rounded-md bg-primary">
            Sign Out
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default AttendanceCard;